import type { IncomingHttpHeaders } from "node:http2";
import type { ILogger } from "@pandas/logger";
import { ApiError, NetworkError } from "./types";

export type { ApiResponse } from "./types";
export { AbortError, ApiError, isAbortError, NetworkError } from "./types";

type RequestType = "POST" | "PUT" | "DELETE" | "GET";
type RequestHeaders = RequestInit["headers"] & IncomingHttpHeaders;

// Per-request options extend the standard fetch init with an optional timeout.
export type RequestOptions = RequestInit & { timeoutMs?: number };

// Default request timeout. Without a timeout a request whose response is lost after
// the server already processed it (flaky in-store/mobile networks) hangs forever with
// no error — see PND-65. A timeout converts that hang into a retryable failure.
export const DEFAULT_TIMEOUT_MS = 25_000;
const HEADER_PROVIDER_TIMEOUT_MS = 5_000;
const HEADER_PROVIDER_TIMED_OUT = Symbol("header-provider-timed-out");

// The platform still answers a buyer's no-offer as a 500; until it answers a 200 with a
// null payload those codes log at warning, the outcome they are (they logged fatal before).
// This set leaves with that platform change.
const DEFERRED_NO_OFFER_CODES: ReadonlySet<string> = new Set(["PRODUCT_NOT_FOUND", "PRICE_NOT_FOUND"]);
const GENERIC_ERROR_CODE = "GENERIC_ERROR";

type ResponseLogLevel = "info" | "warning" | "error" | "fatal";

/**
 * The log level follows the status category the platform answers with: a 5xx other
 * than 503 is a system fault (fatal), a 503 a provider outage (error), and every
 * 4xx a business outcome (warning) — a 404 included, so a missing record never
 * sinks below a warning-level monitor.
 */
const responseLogLevel = (status: number, errorCode: string | undefined): ResponseLogLevel => {
	if (status < 400) return "info";
	if (status === 503) return "error";
	if (status >= 500) return errorCode !== undefined && DEFERRED_NO_OFFER_CODES.has(errorCode) ? "warning" : "fatal";
	return "warning";
};

const nonEmptyString = (value: unknown): string | undefined =>
	typeof value === "string" && value.length > 0 ? value : undefined;

function combineSignals(...signals: Array<AbortSignal | null | undefined>): AbortSignal | undefined {
	const present = signals.filter((signal): signal is AbortSignal => Boolean(signal));
	if (present.length === 0) return undefined;
	if (present.length === 1) return present[0];
	return AbortSignal.any(present);
}

export default class BaseHttpService {
	private _baseUrl: string;
	private _logger?: ILogger;
	private _headerProvider?: () => Promise<RequestHeaders>;
	private _defaultTimeoutMs: number;
	private _headerProviderTimeoutMs: number;

	constructor(
		baseUrl = "",
		logger: ILogger | undefined = undefined,
		headerProvider?: () => Promise<RequestHeaders>,
		defaultTimeoutMs: number = DEFAULT_TIMEOUT_MS,
		headerProviderTimeoutMs: number = HEADER_PROVIDER_TIMEOUT_MS,
	) {
		this._baseUrl = baseUrl;
		this._logger = logger;
		this._headerProvider = headerProvider;
		this._defaultTimeoutMs = defaultTimeoutMs;
		this._headerProviderTimeoutMs = headerProviderTimeoutMs;
	}

	/**
	 * The provider's await sits outside the request's timeout signal (that only aborts the fetch),
	 * so an acquisition that never settles would stall the request silently and forever. Past the
	 * cap — spent from the request's own budget — the request proceeds without the provided headers
	 * (platform's public v1 routes accept that as a null-user session, not a 4xx) and logs a warning.
	 */
	private async resolveProvidedHeaders(url: string): Promise<RequestHeaders> {
		if (!this._headerProvider) return {};
		let timer: ReturnType<typeof setTimeout> | undefined;
		const timedOut = new Promise<typeof HEADER_PROVIDER_TIMED_OUT>((resolve) => {
			timer = setTimeout(() => resolve(HEADER_PROVIDER_TIMED_OUT), this._headerProviderTimeoutMs);
		});
		try {
			const provided = await Promise.race([this._headerProvider(), timedOut]);
			if (provided !== HEADER_PROVIDER_TIMED_OUT) return provided;
			this._logger?.warning("NETWORK", {
				message: "Header provider timed out; request sent without provided headers",
				action_type: "http",
				route: this._baseUrl + url,
				timeoutMs: this._headerProviderTimeoutMs,
			});
			return {};
		} finally {
			clearTimeout(timer);
		}
	}

	get baseUrl(): string {
		return this._baseUrl;
	}

	private async sendRequest(
		reqType: RequestType,
		logMessage: string,
		url: string,
		body: unknown = null,
		headers: RequestHeaders = {},
		options: RequestOptions = {},
	): Promise<unknown> {
		let bodyParams: BodyInit | null = null;
		const { timeoutMs: optionTimeoutMs, signal: userSignal, ...fetchOptions } = options;
		const timeoutMs = optionTimeoutMs ?? this._defaultTimeoutMs;
		const timeoutSignal = timeoutMs > 0 ? AbortSignal.timeout(timeoutMs) : undefined;
		const signal = combineSignals(userSignal, timeoutSignal);

		// The ternary keeps the no-provider path free of an extra microtask, preserving same-tick
		// fetch dispatch: the timeout suite's hanging-fetch fake settles only via an abort event,
		// which an already-aborted signal never fires (real fetch rejects such a signal outright).
		const providedHeaders = this._headerProvider ? await this.resolveProvidedHeaders(url) : {};
		const mutableHeaders =
			typeof headers === "object" && headers !== null
				? { ...providedHeaders, ...(headers as RequestHeaders) }
				: { ...providedHeaders };

		if (body instanceof FormData) {
			bodyParams = body;
		} else if (body instanceof URLSearchParams) {
			mutableHeaders["Content-Type"] = "application/x-www-form-urlencoded";
			bodyParams = body;
		} else if (
			typeof body === "string" ||
			body instanceof Blob ||
			body instanceof ArrayBuffer ||
			ArrayBuffer.isView(body)
		) {
			bodyParams = body as BodyInit;
		} else if (body !== null && typeof body === "object") {
			bodyParams = JSON.stringify(body);
			mutableHeaders["Content-Type"] = "application/json";
		}

		const reqUrl = this._baseUrl + url;
		const startTime = performance.now();

		try {
			const mergedHeaders = new Headers(mutableHeaders as HeadersInit);
			if (options.headers) {
				const optionHeaders = new Headers(options.headers as HeadersInit);
				optionHeaders.forEach((value, key) => mergedHeaders.set(key, value));
			}

			const response = await fetch(reqUrl, {
				method: reqType,
				...fetchOptions,
				headers: mergedHeaders,
				body: bodyParams,
				signal,
			});
			const durationMs = Math.round(performance.now() - startTime);

			let respJson: ReturnType<typeof JSON.parse> | null = null;
			let serializationError: unknown = null;
			if (response.headers.get("content-type")?.includes("application/json")) {
				try {
					respJson = await response.json();
				} catch (error) {
					serializationError = error;
				}
			}

			const answeredCode = nonEmptyString(respJson?.errorCode);
			if (this._logger) {
				const level: ResponseLogLevel = response.ok ? "info" : responseLogLevel(response.status, answeredCode);
				const logFunc: ILogger["info"] = this._logger[level];
				logFunc(
					"NETWORK",
					{ message: logMessage, action_type: "http", status: response.status, route: reqUrl, duration_ms: durationMs },
					reqType,
					body,
					respJson,
					serializationError,
				);
			}

			if (!response.ok) {
				// The code never falls back to the message: a missing one is the generic code the
				// platform itself answers; an empty message falls back to the code, so nothing renders blank.
				const errorCode = answeredCode ?? GENERIC_ERROR_CODE;
				const message = nonEmptyString(respJson?.message) ?? (respJson ? errorCode : "Unknown error");
				const data =
					typeof respJson?.data === "object" && respJson.data !== null
						? (respJson.data as Record<string, unknown>)
						: undefined;
				throw new ApiError(message, { errorCode, status: response.status, data });
			}

			return respJson;
		} catch (error) {
			// Genuine user-initiated abort (e.g. navigation): propagate untouched so callers can no-op.
			if (userSignal?.aborted) {
				throw error;
			}
			const durationMs = Math.round(performance.now() - startTime);
			if (!(error instanceof ApiError)) {
				// A timeout-induced abort is a network failure, not a user abort — surface it as a
				// retryable NetworkError so retry logic re-attempts and stalls become visible errors.
				const isTimeout = timeoutSignal?.aborted ?? false;
				const message = isTimeout ? `Request timed out after ${timeoutMs}ms` : "Network request failed (fetch error)";
				this._logger?.error(
					"NETWORK",
					{
						message,
						action_type: "http",
						route: reqUrl,
						duration_ms: durationMs,
					},
					reqType,
					body,
					error,
				);
				throw new NetworkError(isTimeout ? message : error instanceof Error ? error.message : "fetch failed");
			}
			throw error;
		}
	}

	public async get<T>(logMessage: string, url: string, headers?: RequestHeaders, options?: RequestOptions): Promise<T> {
		return this.sendRequest("GET", logMessage, url, null, headers, options) as Promise<T>;
	}

	public async post<T>(
		logMessage: string,
		url: string,
		body: unknown = null,
		headers?: RequestHeaders,
		options?: RequestOptions,
	): Promise<T> {
		return this.sendRequest("POST", logMessage, url, body, headers, options) as Promise<T>;
	}

	public async put<T>(
		logMessage: string,
		url: string,
		body: unknown = null,
		headers?: RequestHeaders,
		options?: RequestOptions,
	): Promise<T> {
		return this.sendRequest("PUT", logMessage, url, body, headers, options) as Promise<T>;
	}

	public async delete<T>(
		logMessage: string,
		url: string,
		body: unknown = null,
		headers?: RequestHeaders,
		options?: RequestOptions,
	): Promise<T> {
		return this.sendRequest("DELETE", logMessage, url, body, headers, options) as Promise<T>;
	}
}
